/* eslint-disable indent */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/styles';
import { Card, CardContent, Button, TextField, Grid, Typography } from '@material-ui/core';
import { RegEx } from 'utils';

const useStyles = makeStyles((theme) => ({
  root: { overflow: 'visible' },
  alert: {
    marginBottom: theme.spacing(3),
  },
  formGroup: {
    marginBottom: theme.spacing(3),
    width: '100%',
    flexGrow: 1,
  },
  formGroupGrid: {
    marginBottom: theme.spacing(3),
    flexGrow: 1,
  },
  fieldGroup: {
    display: 'flex',
    alignItems: 'center',
  },
  fieldHint: {
    margin: theme.spacing(1, 0),
  },
  addButton: {
    marginLeft: theme.spacing(1),
  },
  tags: {
    marginTop: theme.spacing(1),
    '& > * + *': {
      marginLeft: theme.spacing(1),
    },
  },
  flexGrow: {
    flexGrow: 1,
  },
  modal: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '40%',
    [theme.breakpoints.down('md')]: {
      width: '90%',
    },
    bgcolor: 'background.white',
    boxShadow: 24,
  },
  modalActionCancel: {
    marginLeft: '2%',
  },
  modalActionPrimary: {
    marginLeft: '5%',
  },
}));

const OrganizationEditModalForm = ({ organization, submitEdit, hide }) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const editOrganization = !!organization.id;

  const [newOrganizationValues, setNewOrganizationValues] = useState(
    editOrganization
      ? {
          name: organization.name === undefined ? '' : organization.name,
          audience: organization.audience === undefined ? '' : organization.audience,
          description: organization.description === undefined ? '' : organization.description,
        }
      : {
          name: '',
          audience: '',
          description: '',
        }
  );

  const [formErrors, setFormErrors] = useState({
    name: '',
    audience: '',
  });

  const handleFieldChange = (event, field, value) => {
    setNewOrganizationValues({ ...newOrganizationValues, [field]: value });
  };

  const validate = (form) => {
    let valid = true;
    const errorBuilder = {
      name: formErrors.name,
      audience: formErrors.audience,
    };

    if (!form.name || form.name === '') {
      valid = false;
      errorBuilder.name = 'Name is a Required Field.';
    } else {
      errorBuilder.name = '';
    }

    if (form.audience.trim().length < 1) {
      valid = false;
      errorBuilder.audience = 'Audience is a Required Field.';
    } else if (!RegEx.isDomain(form.audience) || form.audience.includes('www.')) {
      valid = false;
      errorBuilder.audience = 'Audience must be a well-formed domain.';
    } else {
      errorBuilder.audience = '';
    }

    setFormErrors(errorBuilder);

    return valid;
  };

  const handleSubmit = () => {
    const organizationValues = {
      name: newOrganizationValues.name,
      audience: newOrganizationValues.audience,
      description: newOrganizationValues.description,
    };

    if (validate(organizationValues)) {
      submitEdit(organizationValues);
      hide();
    }
  };

  const checkInput = (event, field) => {
    let errorBuilder = {
      name: formErrors?.name ? formErrors.name : '',
      audience: formErrors?.audience ? formErrors.audience : '',
    };

    if (field === 'name') {
      if (!event.target.value || event.target.value.trim() === '') {
        errorBuilder.name = t('required');
      } else {
        errorBuilder.name = '';
      }
    }

    if (field === 'audience') {
      if (!event.target.value || event.target.value.trim() === '') {
        errorBuilder.audience = t('required');
      } else if (!RegEx.isDomain(event.target.value)) {
        errorBuilder.audience = 'Audience must be a well-formed domain.';
      } else {
        errorBuilder.audience = '';
      }
    }

    setFormErrors(errorBuilder);
  };

  return (
    <Card className={classes.modal}>
      <CardContent>
        <form
          onSubmit={() => {
            handleSubmit();
          }}
        >
          <Grid container spacing={3} alignItems="stretch">
            <Grid item xs={12} sm={12}>
              <Typography variant="h3">
                {editOrganization ? t('organization_modify') : t('organization_create')}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={12}>
              <div className={classes.formGroupGrid}>
                <TextField
                  label={t('organization_name')}
                  name="name"
                  autoComplete="name"
                  onChange={(event) => handleFieldChange(event, 'name', event.target.value)}
                  value={newOrganizationValues.name}
                  placeholder={t('organization_name')}
                  helperText={formErrors.name ? formErrors.name : ' '}
                  error={!!formErrors.name}
                  inputProps={{
                    maxLength: 63,
                  }}
                  onBlur={(event) => {
                    checkInput(event, 'name');
                  }}
                  onKeyDown={(event) => {
                    checkInput(event, 'name');
                  }}
                  required
                />
              </div>
            </Grid>
            <Grid item xs={12} sm={12}>
              <div className={classes.formGroupGrid}>
                <TextField
                  label={t('organization_audience')}
                  name="audience"
                  autoComplete="audience"
                  onChange={(event) => handleFieldChange(event, 'audience', event.target.value)}
                  value={newOrganizationValues.audience}
                  placeholder={t('organization_audience')}
                  helperText={formErrors.audience ? formErrors.audience : t('audience_example')}
                  error={!!formErrors.audience}
                  inputProps={{
                    maxLength: 127,
                  }}
                  onBlur={(event) => {
                    checkInput(event, 'audience');
                  }}
                  onKeyDown={(event) => {
                    checkInput(event, 'audience');
                  }}
                  required
                />
              </div>
            </Grid>
            <Grid item xs={12} sm={12}>
              <div className={classes.formGroupGrid}>
                <TextField
                  label={t('organization_description')}
                  name="description"
                  autoComplete="description"
                  onChange={(event) => handleFieldChange(event, 'description', event.target.value)}
                  value={newOrganizationValues.description}
                  placeholder={t('organization_description')}
                  multiline
                  minRows={5}
                  inputProps={{
                    maxLength: 1023,
                  }}
                  required
                />
              </div>
            </Grid>
          </Grid>
          <Button onClick={hide} color="primary" className={classes.modalActionCancel}>
            {t('cancel')}
          </Button>
          <Button onClick={handleSubmit} color="primary" variant="contained" className={classes.modalActionPrimary}>
            {t('submit')}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

OrganizationEditModalForm.propTypes = {
  organization: PropTypes.object,
  fields: PropTypes.object,
  submitEdit: PropTypes.func.isRequired,
  hide: PropTypes.func.isRequired,
  show: PropTypes.bool.isRequired,
};

OrganizationEditModalForm.defaultProps = {
  organization: {},
};

export default OrganizationEditModalForm;
